import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  createEmployeeSigningRequest,
  createServiceAgreementSigningRequest,
  createSigningRequest,
  downloadSignedDocument,
  fetchSigningRequests,
  pollSigningRequest,
} from "../../../api/signing";
import { PMButton } from "../../utils/PMButton";

const statusClass = {
  completed: "bg-green-100 text-green-800",
  signed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  rejected: "bg-red-100 text-red-800",
  expired: "bg-red-100 text-red-800",
  sent: "bg-blue-100 text-blue-800",
  in_progress: "bg-blue-100 text-blue-800",
  queued: "bg-yellow-100 text-yellow-800",
};

export function SigningPanel({
  signableType,
  signableId,
  title,
  nonSensitiveTitle,
  description,
  defaultSigner = {},
  serviceAgreementId,
  userId,
  employeeId,
}) {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    name: defaultSigner.name || "",
    national_identity_number: defaultSigner.national_identity_number || "",
    email: defaultSigner.email || "",
    mobile: defaultSigner.mobile || "",
  });

  useEffect(() => {
    setForm({
      name: defaultSigner.name || "",
      national_identity_number: defaultSigner.national_identity_number || "",
      email: defaultSigner.email || "",
      mobile: defaultSigner.mobile || "",
    });
  }, [defaultSigner.name, defaultSigner.national_identity_number, defaultSigner.email, defaultSigner.mobile]);

  useEffect(() => {
    if (signableId) loadRequests();
  }, [signableType, signableId]);

  const latestRequest = useMemo(() => requests?.[0], [requests]);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const result = await fetchSigningRequests({
        signable_type: signableType,
        signable_id: signableId,
      });
      setRequests(result?.data || []);
    } catch (error) {
      toast.error(t("signing.errors.load_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setIsSending(true);
    try {
      const payload = {
        signable_type: signableType,
        signable_id: signableId,
        title,
        non_sensitive_title: nonSensitiveTitle || title,
        description,
        signers: [
          {
            name: form.name,
            national_identity_number: form.national_identity_number,
            email: form.email,
            mobile: form.mobile,
          },
        ],
      };

      if (serviceAgreementId) {
        await createServiceAgreementSigningRequest(serviceAgreementId, payload);
      } else if (userId && employeeId) {
        await createEmployeeSigningRequest(userId, employeeId, payload);
      } else {
        await createSigningRequest(payload);
      }

      toast.success(t("signing.created"));
      setFormOpen(false);
      await loadRequests();
    } catch (error) {
      const errors = error?.response?.data?.errors;
      toast.error(Array.isArray(errors) ? errors.join(", ") : t("signing.errors.create_failed"));
    } finally {
      setIsSending(false);
    }
  };

  const handlePoll = async (requestId) => {
    try {
      const result = await pollSigningRequest(requestId);
      setRequests((prev) => prev.map((item) => (item.id === result.id ? result : item)));
      toast.success(t("signing.refreshed"));
    } catch (error) {
      toast.error(t("signing.errors.poll_failed"));
    }
  };

  const handleDownload = async (requestId) => {
    try {
      const response = await downloadSignedDocument(requestId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `signed-document-${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("signing.errors.download_failed"));
    }
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("signing.title")}</h3>
          <p className="mt-1 text-sm text-gray-500">{t("signing.description")}</p>
        </div>
        <div className="w-44">
          <PMButton type="button" onClick={() => setFormOpen((open) => !open)}>
            {t("signing.new_request")}
          </PMButton>
        </div>
      </div>

      {latestRequest && (
        <div className="mt-4 rounded-md bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">{latestRequest.title}</div>
              <div className="text-xs text-gray-500">
                {t("signing.sender_reference")}: {latestRequest.sender_reference}
              </div>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusClass[latestRequest.status] || "bg-gray-100 text-gray-800"}`}>
              {t(`signing.status.${latestRequest.status}`, latestRequest.status)}
            </span>
          </div>

          <div className="mt-3 space-y-2">
            {(latestRequest.signers || []).map((signer) => (
              <div key={signer.id} className="flex items-center justify-between text-sm">
                <span>{signer.name || signer.email || signer.mobile || signer.national_identity_number_masked}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass[signer.status] || "bg-gray-100 text-gray-700"}`}>
                  {t(`signing.status.${signer.status}`, signer.status)}
                </span>
              </div>
            ))}
          </div>

          {latestRequest.error_message && (
            <div className="mt-3 rounded bg-red-50 p-2 text-sm text-red-700">{latestRequest.error_message}</div>
          )}

          <div className="mt-4 flex gap-2">
            <PMButton type="button" styleType="minimal" onClick={() => handlePoll(latestRequest.id)}>
              {t("signing.refresh_status")}
            </PMButton>
            <PMButton
              type="button"
              styleType="minimal"
              disabled={latestRequest.status !== "completed"}
              onClick={() => handleDownload(latestRequest.id)}
            >
              {t("signing.download_signed")}
            </PMButton>
          </div>
        </div>
      )}

      {!latestRequest && !isLoading && (
        <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-600">{t("signing.empty")}</div>
      )}

      {formOpen && (
        <form onSubmit={handleCreate} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label={t("signing.signer_name")} name="name" value={form.name} onChange={handleChange} />
          <Input
            label={t("signing.national_identity_number")}
            name="national_identity_number"
            value={form.national_identity_number}
            onChange={handleChange}
            autoComplete="off"
          />
          <Input label={t("signing.email")} name="email" value={form.email} onChange={handleChange} type="email" />
          <Input label={t("signing.mobile")} name="mobile" value={form.mobile} onChange={handleChange} />
          <div className="sm:col-span-2 rounded bg-yellow-50 p-2 text-sm text-yellow-800">
            {t("signing.identity_warning")}
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <div className="w-44">
              <PMButton type="submit" loading={isSending}>
                {t("signing.send")}
              </PMButton>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-gray-700">{label}</span>
      <input
        {...props}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
      />
    </label>
  );
}
